import React, { createContext, useContext, useEffect, useState } from 'react';

/*
 * VelaThemeContext — Vela + shared AraVela landing theme state.
 * - Theme state: 'light' (default) | 'dark'
 * - Persistence: localStorage key 'vela_theme' (non-sensitive UI preference;
 *   works before any encryption key is available, i.e. on Landing/Start/Unlock)
 * - Applies data-vela-theme attribute on <html> (document.documentElement).
 * - Does NOT auto-detect prefers-color-scheme; existing users remain in light
 *   until they manually choose dark.
 * - Fully independent of AraThemeContext. Ara pages set their own
 *   background/color via .ara-root CSS, so Vela's attribute cannot bleed
 *   into Ara.
 */

const STORAGE_KEY = 'vela_theme';
const VelaThemeContext = createContext(null);

export const useVelaTheme = () => {
  const ctx = useContext(VelaThemeContext);
  if (!ctx) throw new Error('useVelaTheme must be used within VelaThemeProvider');
  return ctx;
};

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch (e) { /* storage unavailable; fall through */ }
  return 'light';
};

export const VelaThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStored);

  // Apply attribute whenever theme changes, including on first mount.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-vela-theme', theme);
    } catch (e) { /* non-DOM env; ignore */ }
  }, [theme]);

  const setTheme = (next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* ignore */ }
  };

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return (
    <VelaThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </VelaThemeContext.Provider>
  );
};
